import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiEdit2, FiTrash2, FiSearch } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { toast, confirmDelete, showError } from '../../utils/alert';
import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import BulkActions from '../../components/common/BulkActions';
import Pagination from '../../components/common/Pagination';
import StatusBadge from '../../components/common/StatusBadge';
import { useAuth } from '../../context/AuthContext';

export default function Suppliers() {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [params, setParams] = useState({ page: 1, search: '', per_page: 100 });
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.suppliers.list(params);
      const raw = Array.isArray(data.data) ? data.data : data.suppliers || data;
      const list = Array.isArray(raw) ? raw : raw && Array.isArray(raw.data) ? raw.data : [];
      setRows(list);
      setMeta(raw && !Array.isArray(raw)
        ? { current_page: raw.current_page, last_page: raw.last_page, total: raw.total, per_page: raw.per_page, per_page_options: data.per_page_options || raw.per_page_options }
        : null);
    } catch (err) {
      showError('Failed to load suppliers', extractError(err));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (row) => {
    const res = await confirmDelete(`supplier "${row.name}"`);
    if (!res.isConfirmed) return;
    try {
      await api.suppliers.remove(row.id);
      toast('success', '', 'Supplier deleted');
      load();
    } catch (err) {
      showError('Failed to delete supplier', extractError(err));
    }
  };

  return (
    <div className="page">
      <PageHeader
        title="Suppliers"
        subtitle="Manage supplier records."
        crumbs={[{ label: 'Finance' }, { label: 'Suppliers' }]}
        actions={hasPermission('create-suppliers') && (
          <button className="btn btn-primary" onClick={() => navigate('/suppliers/new')}><FiPlus /> Add Supplier</button>
        )}
      />

      <div className="card">
        <div className="card-body">
          <div className="filters-bar">
            <div className="topbar-search">
              <FiSearch className="search-icon" />
              <input
                placeholder="Search suppliers..."
                value={params.search}
                onChange={(e) => setParams({ ...params, search: e.target.value, page: 1 })}
              />
            </div>
          </div>

          <BulkActions selectedIds={selectedIds} onClear={() => setSelectedIds([])} onDone={load} deleteType="suppliers" label="suppliers" />
          <DataTable
            loading={loading}
            data={rows}
            emptyTitle="No suppliers found"
            selectable
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            columns={[
              { key: 'name', label: 'Supplier', render: (r) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div className="user-avatar" style={{ width: 32, height: 32, fontSize: 13, borderRadius: 8 }}>
                    {r.name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600 }}>{r.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{r.phone || r.email || '-'}</div>
                  </div>
                </div>
              ) },
              { key: 'email', label: 'Email' },
              { key: 'gst_no', label: 'GST No.', render: (r) => r.gst_no || '-' },
              { key: 'ntn_no', label: 'NTN No.', render: (r) => r.ntn_no || '-' },
              { key: 'account', label: 'Linked Account', render: (r) => r.account ? r.account.name : <span className="badge badge-yellow">No account</span> },
              { key: 'is_active', label: 'Status', render: (r) => <StatusBadge status={r.is_active ? 'active' : 'inactive'} /> },
              { key: 'id', label: '', align: 'right', render: (r) => (
                <div className="actions">
                  {hasPermission('update-suppliers') && <button className="btn-icon" onClick={() => navigate(`/suppliers/${r.id}/edit`)}><FiEdit2 /></button>}
                  {hasPermission('delete-suppliers') && <button className="btn-icon danger" onClick={() => handleDelete(r)}><FiTrash2 /></button>}
                </div>
              ) },
            ]}
          />
          {meta && <Pagination meta={meta} onPage={(p) => setParams({ ...params, page: p })} onPerPage={(n) => setParams({ ...params, per_page: n, page: 1 })} />}
        </div>
      </div>
    </div>
  );
}
